import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { runInAction, toJS } from 'mobx';
import { useQueryParams } from 'global/hooks/useQuery';
import { useHistory } from 'react-router-dom';
import {
  BrowsingCard,
  FilterSidebar,
  EditableInfoBlock,
  ComparisonBlock,
  ProposalSidebar,
  Button,
  FilterArrowButton,
} from '@insurancemenu/imenu-components-lib';
import { Filter } from '@insurancemenu/imenu-components-lib/dist/types/filterSidebarTypings';
import { BrowsingPageType, Portfolio } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { ArrowBack, Cart, Legal } from '@insurancemenu/imenu-components-lib/dist/icons';
import clsx from 'clsx';
import FormsSpinner from 'components/common/general/FormSpinner';
import DynamicFormModal from 'components/common/modals/DynamicFormModal';
import { breakpointsValues, colors } from '@insurancemenu/imenu-components-lib/dist/utils';
import useWindowDimensions from '@insurancemenu/imenu-components-lib/dist/hooks/useWindowDimensions';
import { INITIAL_FILTER } from 'store/ui-stores/browsing/browsingFilterStore';
import { StandardFormChangeEvent } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { createStyles } from './styles';
import { createStyles as createMobileViewClasses } from './MobileBrowsingContent/styles';
import ResetBlock from './ResetBlock';
import GlobalSidebarsLayout from '../GlobalSidebarsLayout';
import MobileBrowsingContent from './MobileBrowsingContent';
import ContinuousRatesPullingModal from './ContinuousRatesPullingModal';

export interface BrowsingPageTypeInfo {
  backButtonTitle: string;
  filterSidebarTitle: string;
  backButtonFunc: () => void;
}
export type BrowsingTypesMap = {
  [key in BrowsingPageType]: BrowsingPageTypeInfo;
};

const BrowsingContent = observer((): JSX.Element => {
  // Mobx store
  const {
    browsingStore: {
      productLineAttrs,
      marketingTags,
      productLines,
      group,
      cart,
      allPortfolios,
      comparedPortfolios,
      filteredPortfolios,
      rateValues,
      currentProductLineId,
      loading,
      continuousPullingRatesModalOpened,
      calculateRatesAttempts,
      // get all data
      getPortfolios,
      getCatalog,
      collectInitialData,
      // browsing card methods
      onAddToProposal,
      onRemoveFromProposal,
      onViewPlanDetails,
      onCompare,
      compareVariants,
      compareAllPortfoliosInPl,
      onRemoveFromComparison,
      clearComparedPortfolios,
      compareSelectedPortfolios,
      compareAllPortfolios,
      updateCart,
      setCurrentProductLineId,
      setFilteredPortfolios,
      setLoading,
      isProposalSidebarOpened,
      setProposalSidebarOpeningStatus,
      resetData: resetInitialData,
      onConfigurePortfolio,
      getAllCarriersFromProducts,
      setContinuousPullingRatesModal,
    },
    browsingFilterStore: { filters: helpMeDecideFilters, filterPortfoliosBy, areFiltersSet, setFilters },
    browsingCriteriaModalStore: {
      openBrowsingCriteriaModal,
      closeBrowsingCriteriaModal,
      saveBrowsingCriteria,
      showBrowsingCriteriaModal,
      dynamicForm,
      form,
      loading: changeBcModalLoading,
    },
    domainStore: { domainHeader, getEStorePrerequisiteFormId },
    helpMeDecideStore: { setCriteriaIndex },
  } = useRootStore();

  const { cartItems, lastUpdateBrowsingCriteria, id } = cart;

  const convertedAllPortfolios = allPortfolios.map((portfolio) => toJS(portfolio)).slice();
  const convertedFilteredPortfolios = filteredPortfolios.map((portfolio) => toJS(portfolio)).slice();
  const convertedMarketingTags = marketingTags.map((tag) => toJS(tag)).slice();
  const convertedProductLineAttrs = productLineAttrs.map((attr) => toJS(attr)).slice();
  const convertedProductLines = productLines.map((line) => toJS(line)).slice();
  const convertedCartItems = cartItems.map((item) => toJS(item)).slice();
  const convertedComparedPortfolios = comparedPortfolios.map((portfolio) => toJS(portfolio)).slice();
  const convertedGroup = toJS(group);
  const convertedRateMaxValues = toJS(rateValues);
  const convertedBC = toJS(lastUpdateBrowsingCriteria);
  const convertedDomain = toJS(domainHeader);
  const convertedHelpMeDecideFilters = toJS(helpMeDecideFilters);

  const convertedComparedPortfoliosIds = convertedComparedPortfolios.map((portfolio) => portfolio.id);
  const productLineIds = convertedProductLines.map((line) => line.productLineId);
  const areHelpMeDecideFiltersSet = areFiltersSet(convertedHelpMeDecideFilters);

  const [isFilterReset, setIsFilterReset] = useState(false);
  const query = useQueryParams();
  const history = useHistory();

  // Show different layout for mobile and tablet view
  const { width: windowWidth } = useWindowDimensions();
  const isMobileView = windowWidth <= 768;

  const groupId = query.get('groupId') || '';
  const cartId = query.get('cartId') || null;
  const backType = query.get('backType') || null;

  const rateSegment = currentProductLineId ? PRODUCT_LINES_CONTENT_MAP[currentProductLineId].rateSegment : 'GROUP';
  const effectiveDate = convertedBC?.effectiveDate;
  const zipCode = convertedBC?.zipCode;
  const state = convertedBC?.state;

  const classes = createStyles({ comparedPortfoliosQuantity: convertedComparedPortfolios.length });
  const mobileViewClasses = createMobileViewClasses();

  const productLineIdFromUrl = (): ProductLineId => {
    return query.get('productLineId') ? (query.get('productLineId') as ProductLineId) : 'GroupDental';
  };

  const eStorePrerequisiteFormId = getEStorePrerequisiteFormId(convertedProductLines, productLineIdFromUrl());

  const resetFilter = (): void => {
    // reset all filter changes
    setIsFilterReset(true);
    setFilteredPortfolios(allPortfolios); // reset filtered portfolios
  };
  const resetData = (): void => {
    resetInitialData(resetFilter);
  };

  // Action when u clicks on the Product lines in the Sidebar
  // On click we change Product Line Id in the URL, so we will know what Browsing Criteria modal to open
  // But in the store we will save new Product Line Id, only after submitting the modal
  const changeProductLine = (selectedProductLineId: ProductLineId): void => {
    setCurrentProductLineId(selectedProductLineId); // we firstly change product line id in the URL and then update the content of the page

    const pLine = convertedProductLines.find((line) => line.productLineId === selectedProductLineId);
    void openBrowsingCriteriaModal(cart, selectedProductLineId, pLine?.eStoreBcFormId || pLine?.bcFormId || '');
  };

  useEffect(() => {
    if (cartId && !areHelpMeDecideFiltersSet) {
      void collectInitialData(groupId, cartId, productLineIdFromUrl());
    }
    return (): void => resetData();
  }, []);

  useEffect(() => {
    if (isMobileView) {
      setProposalSidebarOpeningStatus(false);
    }
  }, [isMobileView]);

  useEffect(() => {
    if (convertedProductLines.length > 0 && id !== '') {
      const productLineId = productLineIdFromUrl();
      setCurrentProductLineId(productLineId);
      // The form is displayed on the popular plans page with a small set of questions and already has bc.
      // However, it should be shown again because there is a list of other bc that have not yet been selected.
      if (!convertedBC[productLineId] || eStorePrerequisiteFormId) {
        changeProductLine(productLineId);
      }
    }
  }, [productLines]);

  const filterPortfolios = (filters: Filter): void => {
    // update reset status. It allows filter component save and send values
    setIsFilterReset(false);
    // On every new filter value, we should search available portfolios from full list
    const updatedPortfolios = filterPortfoliosBy([...convertedAllPortfolios], filters, convertedProductLineAttrs);

    setFilteredPortfolios(updatedPortfolios);
  };

  const removePortfolioFromProposal = (portfolioId: string): void => {
    void onRemoveFromProposal(portfolioId, true);
  };

  // Action in the header of the page
  const openEditBC = (): void => {
    changeProductLine(currentProductLineId || productLineIdFromUrl());
  };

  const saveModalChanges = async (): Promise<void> => {
    if (!currentProductLineId) throw new Error('Modal cannot be opened without currentProductLineId in the store');
    closeBrowsingCriteriaModal();
    setLoading(true);
    clearComparedPortfolios();

    const updatedCart = await saveBrowsingCriteria();
    await runInAction(async () => {
      updateCart(updatedCart);
      if (currentProductLineId !== productLineIdFromUrl()) {
        history.push(
          `/browsing?groupId=${groupId}&cartId=${cartId!}&productLineId=${currentProductLineId}&cartType=browsing&backType=${backType!}`,
        );
      }
      const requests = [
        getCatalog(effectiveDate, rateSegment, currentProductLineId, state),
        getPortfolios({
          bc: updatedCart.lastUpdateBrowsingCriteria,
          rateSegment,
          sponsorId: convertedDomain.id,
          agencyId: '',
          productLineId: currentProductLineId,
          groupId,
        }),
      ];
      await Promise.all(requests).then(() => {
        setIsFilterReset(true); // reset all filters if they were set for previous product line
        setLoading(false);
      });
    });
  };

  const cancelModalChanges = (): void => {
    if (currentProductLineId !== productLineIdFromUrl()) {
      setCurrentProductLineId(productLineIdFromUrl()); // we firstly change product line id in the URL and then update the content of the page
    }
    closeBrowsingCriteriaModal();
  };

  const ProposalSidebarButton = (openingStatus: boolean): JSX.Element => {
    return (
      <Button
        color="primary"
        onClick={(): void => {
          setProposalSidebarOpeningStatus(openingStatus);
        }}
        size="medium"
        leftIconProp={<Cart />}
      >
        {`${convertedCartItems.length} in Proposal`}
      </Button>
    );
  };

  const changeBcModalCancel = (): JSX.Element => {
    return convertedFilteredPortfolios.length === 0 ? (
      <Button
        type="outlined"
        onClick={(): void => {
          history.goBack();
          closeBrowsingCriteriaModal();
        }}
        size="large"
      >
        Go Back
      </Button>
    ) : (
      <Button type="outlined" onClick={cancelModalChanges} size="large">
        Cancel
      </Button>
    );
  };

  const showPortfoliosContent = (list: Portfolio[]): JSX.Element[] | JSX.Element => {
    if (list.length === 0 && !loading) {
      return <ResetBlock resetFilter={resetFilter} />;
    }
    const portfoliosList = list.map((portfolio): JSX.Element => {
      const currentCartItem = convertedCartItems.find(({ portfolioId }) => portfolioId === portfolio.id);
      return (
        <BrowsingCard
          key={portfolio.id}
          portfolio={portfolio}
          cartId={cartId || ''}
          productLineAttributes={convertedProductLineAttrs}
          marketingTags={convertedMarketingTags}
          cartItem={currentCartItem}
          onAddToProposal={onAddToProposal}
          onRemoveFromProposal={removePortfolioFromProposal}
          onCompare={onCompare}
          onCompareVariants={(portfolioId: string, currentCartId: string): void => {
            compareVariants(portfolioId, currentCartId, history.push.bind(history));
          }}
          onViewPlanDetails={(portfolioId: string, currentCartId: string): void => {
            onViewPlanDetails(portfolioId, currentCartId, history.push.bind(history));
          }}
          onConfigurePortfolio={
            portfolio.configurable
              ? (portfolioId: string): void => {
                  onConfigurePortfolio(portfolioId, cartId || '', history.push.bind(history));
                }
              : undefined
          }
          selectedForCompare={convertedComparedPortfoliosIds.includes(portfolio.id)}
          breakpoint={breakpointsValues.md}
          className={isMobileView ? mobileViewClasses.browsingCard : classes.browsingCard}
        />
      );
    });

    return <div className={classes.cardsList}>{portfoliosList}</div>;
  };

  const onCartRedirect = (): void => {
    history.push(`/cart?groupId=${groupId}&cartId=${cartId!}&cartType=browsing`);
  };

  const onBackRedirect = (): void => {
    resetData();
    // Help me decide flow may not exist for some product lines
    const isHelpMeDecideFlowExist =
      convertedProductLines.find((pLine) => pLine.productLineId === currentProductLineId)?.eStoreHelpMeDecide || false;

    if (backType === 'help-me-decide' && isHelpMeDecideFlowExist) {
      // Set first criteria for help me decide flow and reset filters
      setCriteriaIndex(1);
      setFilters(INITIAL_FILTER);
      history.push(
        `/help-me-decide?groupId=${groupId}&productLineId=${currentProductLineId!}&cartId=${cartId!}&cartType=help-me-decide`,
      );
    } else {
      history.push(
        `/popular-plans?groupId=${groupId}&productLineId=${currentProductLineId!}&cartId=${cartId!}&cartType=popular`,
      );
    }
  };

  const onProductLineChange = (selectedProductLineId: ProductLineId): void => {
    if (currentProductLineId !== selectedProductLineId) {
      changeProductLine(selectedProductLineId);
    }
  };

  const closeRatesModal = (): void => {
    setContinuousPullingRatesModal(false);
  };

  // This check is needed because of Map type of browsing criteria */
  const EditableBlock =
    !!cart.lastUpdateBrowsingCriteria.shortName && !!currentProductLineId ? (
      <EditableInfoBlock
        title={cart.lastUpdateBrowsingCriteria.shortName[currentProductLineId]}
        onEditInfo={openEditBC}
        className={classes.bcBlock}
      />
    ) : (
      <></>
    );

  const CompareAllButton = (
    <Button
      type="text"
      color="primary"
      leftIconProp={<Legal />}
      size="small"
      className={classes.compareButton}
      onClick={(): void => {
        compareAllPortfolios(history.push.bind(history));
      }}
    >
      {`Compare all ${PRODUCT_LINES_CONTENT_MAP[currentProductLineId || productLineIdFromUrl()].label} Plans`}
    </Button>
  );
  const BrowsingPlans = (
    <div className={classes.browsingContent}>
      <p className={classes.portfoliosQuantity}>
        Available Products: <b>{convertedFilteredPortfolios.length}</b>
      </p>
      {showPortfoliosContent(convertedFilteredPortfolios)}
    </div>
  );

  const Filters = (
    <FilterSidebar
      marketingTags={convertedMarketingTags}
      productLineAttributes={convertedProductLineAttrs}
      rates={convertedRateMaxValues}
      filterPortfolios={filterPortfolios}
      carriers={getAllCarriersFromProducts(convertedAllPortfolios)}
      productLineIds={productLineIds}
      groupInfo={{
        groupName: convertedGroup.name,
        effectiveDate,
        address: `${state} ${zipCode}`,
      }}
      onProductLineChange={onProductLineChange}
      shouldBeReset={isFilterReset}
      activeProductLineId={currentProductLineId}
      className={isMobileView ? mobileViewClasses.sidebarMobileView : ''}
      defaultFilters={convertedHelpMeDecideFilters}
    />
  );
  const ProposalInfo = (additionalButton?: JSX.Element): JSX.Element => (
    <ProposalSidebar
      sidebarTitle="My Cart"
      sidebarStyles={isMobileView ? '' : classes.proposalSidebarContent}
      productLineIds={productLineIds}
      cartItems={convertedCartItems || []}
      submitButton={
        <Button size="large" color="custom" customColor={colors.green[600]} onClick={onCartRedirect}>
          Go to cart
        </Button>
      }
      onProductLineChange={(productLineId: ProductLineId): void => {
        onProductLineChange(productLineId);
        if (isMobileView) {
          setProposalSidebarOpeningStatus(false);
        }
      }}
      onSaveProposal={onCartRedirect}
      onRemoveFromProposal={(portfolioId: string): void => {
        void onRemoveFromProposal(portfolioId, false);
      }}
      isOpen={isProposalSidebarOpened}
      closeButton={isMobileView ? undefined : ProposalSidebarButton(false)}
      onActionClick={(): void => {
        setProposalSidebarOpeningStatus(false);
      }}
      onCompareAllPortfoliosInPl={(productLineId: ProductLineId): void => {
        compareAllPortfoliosInPl(productLineId, history.push.bind(history));
      }}
      additionalButton={additionalButton || undefined}
    />
  );

  return (
    <div className={classes.browsingContentWrapper}>
      {loading && <FormsSpinner display={loading} spinnerStyle={classes.browsingSpinner} />}
      {isMobileView ? (
        <MobileBrowsingContent
          browsingPlans={BrowsingPlans}
          compareAllButton={CompareAllButton}
          editableBlock={EditableBlock}
          resetData={resetFilter}
          onProductLineChange={(e: StandardFormChangeEvent): void => {
            const productLineId = e.target.value as ProductLineId;
            onProductLineChange(productLineId);
          }}
          filter={Filters}
          proposalInfo={ProposalInfo}
          currentProductLineId={currentProductLineId || productLineIdFromUrl()}
          portfoliosQuantity={convertedFilteredPortfolios.length}
        />
      ) : (
        <GlobalSidebarsLayout
          contentStyle={classes.sidebarContentLayout}
          leftSection={Filters}
          centerSection={
            <div>
              <div className={classes.browsingHeader}>
                <div className={classes.headerTop}>
                  <Button
                    leftIconProp={<ArrowBack />}
                    type="text"
                    size="medium"
                    onClick={onBackRedirect}
                    className={classes.backButton}
                  >
                    Back
                  </Button>
                  <FilterArrowButton
                    direction="left"
                    buttonWrapperStyle={clsx(
                      classes.openSidebarButton,
                      isProposalSidebarOpened && classes.hiddenButton,
                    )}
                    button={ProposalSidebarButton(true)}
                    onActionClick={(): void => {
                      setProposalSidebarOpeningStatus(true);
                    }}
                  />

                  {currentProductLineId && (
                    <h2 className={classes.title}>
                      {`Browsing ${PRODUCT_LINES_CONTENT_MAP[currentProductLineId].label}`}
                    </h2>
                  )}
                </div>

                <>
                  {EditableBlock}
                  {CompareAllButton}
                </>
              </div>
              {/* Sticky comparison block */}
              <ComparisonBlock
                comparedPortfolios={convertedComparedPortfolios}
                compareBlockStyles={classes.compareBlock}
                onRemoveFromComparison={onRemoveFromComparison}
                hideCompareBlock={clearComparedPortfolios}
                compareSelectedPortfolios={(): void => {
                  compareSelectedPortfolios(history.push.bind(history));
                }}
              />
              {BrowsingPlans}
            </div>
          }
          rightSection={ProposalInfo()}
        />
      )}
      <DynamicFormModal
        show={showBrowsingCriteriaModal}
        closable={convertedFilteredPortfolios.length !== 0}
        onClose={cancelModalChanges}
        onAction={saveModalChanges}
        dynamicForm={dynamicForm}
        form={form}
        loading={changeBcModalLoading}
        footerCancelButton={changeBcModalCancel()}
        className={classes.bcModal}
      />
      <ContinuousRatesPullingModal
        show={continuousPullingRatesModalOpened}
        onClose={closeRatesModal}
        attemptNumber={calculateRatesAttempts}
      />
    </div>
  );
});

export default BrowsingContent;
